import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { ReactNode, useContext, useEffect, useState } from "react";
import { Table } from "shared/app/Table";
import {
  BankIncomeSource,
  NetIncomeSummary,
  PayrollEmployment,
  PayrollItem,
  SelfReportedVerificationItem,
  VerifiedIncomeSource
} from "types/landlord/reports";
import {
  BankStatementIncomeSource,
  PayStubIncomeSource,
  W2IncomeSources
} from "../types";
import { Show } from "shared/components";
import { VerifiedIncomeSourcesTableExpander } from "./VerifiedIncomeSourcesTableExpander";
import { VerifiedNetEarningsTableExpander } from "./VerifiedNetEarningsTableExpander";
import { SelfReportedIncomeSourcesTableExpander } from "./SelfReportedIncomeSourcesTableExpander";
import { PrintAllContext } from "views/applications-id-report/providers/PrintProvider";

interface UniversalTableProps {
  tableData?:
    | BankIncomeSource[]
    | PayrollItem[]
    | VerifiedIncomeSource[]
    | NetIncomeSummary[]
    | SelfReportedVerificationItem[];
  employment?: PayrollEmployment[];
  sectionName:
    | "Verified income sources"
    | "Verified net earnings"
    | "Self-reported income sources";
  tableColumns: ColumnDef<unknown, any>[];
  rowHeight: number;
  mobileRow?: (row: any) => JSX.Element | ReactNode;
}

export const UniversalTable = ({
  tableData,
  tableColumns,
  sectionName,
  rowHeight,
  employment,
  mobileRow
}: UniversalTableProps) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { documentPrintingState } = useContext(PrintAllContext);
  const table = useReactTable({
    state: {
      expanded: Boolean(documentPrintingState?.length) || expanded
    },
    data: tableData || [],
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    onExpandedChange: setExpanded,
    getRowCanExpand: () => true
  });

  useEffect(() => {
    if (documentPrintingState?.length) setExpanded(true);
  }, [documentPrintingState]);

  return (
    <>
      <Show if={Boolean(tableData?.length)}>
        <Table
          rowHeight={`${rowHeight}px`}
          table={table}
          borderLess
          isCustomExpandedRow={sectionName === "Verified net earnings"}
          expandedRenderer={(row) => {
            if (sectionName === "Verified income sources")
              return (
                <VerifiedIncomeSourcesTableExpander
                  rowData={
                    row.original as
                      | PayStubIncomeSource
                      | BankStatementIncomeSource
                      | W2IncomeSources
                      | BankIncomeSource
                  }
                  employment={employment}
                />
              );

            if (sectionName === "Verified net earnings")
              return (
                <VerifiedNetEarningsTableExpander
                  rowData={row.original as NetIncomeSummary}
                />
              );

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- let's keep it defined like this
            if (sectionName === "Self-reported income sources")
              return (
                <SelfReportedIncomeSourcesTableExpander
                  rowData={row.original as SelfReportedVerificationItem}
                />
              );

            return null;
          }}
          mobileRowClassName="!p-4 max-w-[calc(100vw-1.5rem)]"
          mobileRow={mobileRow}
        />
      </Show>
      <Show if={Boolean(!tableData?.length)}>
        <div className="flex items-center justify-center">
          <p className="text-sm text-content-400">There is no data available</p>
        </div>
      </Show>
    </>
  );
};
